import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1608682285597-156feb50eb4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="font-heading text-4xl font-bold tracking-tighter text-white mb-3">
            mr.polygon.rs
          </h1>
          <p className="text-text-secondary text-base font-body max-w-md">
            Streamlined resource booking for modern workspaces. Reserve meeting rooms, desks, and equipment in seconds.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <h1 className="font-heading text-2xl font-bold tracking-tighter text-white">mr.polygon.rs</h1>
            <p className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary mt-1">/bookings</p>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-medium tracking-tight text-white" data-testid="login-heading">Sign in</h2>
            <p className="text-text-secondary text-sm mt-2 font-body">Enter your credentials to access your workspace.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 mb-6 border border-booked/30 bg-booked/10 text-sm text-booked" data-testid="login-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  required
                  className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  required
                  className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-white text-black font-body font-medium text-sm py-2.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>Sign in <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="text-text-secondary text-sm mt-6 text-center font-body">
            No account?{' '}
            <Link to="/register" className="text-white hover:underline" data-testid="login-register-link">
              Create one
            </Link>
          </p>
          <p className="text-text-secondary text-sm mt-2 text-center font-body">
            <Link to="/forgot-password" className="text-text-secondary hover:text-white hover:underline" data-testid="login-forgot-password-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
